/**
  * Utility function to load a custom font dynamically
  * @param {Object} params - Parameters for loading the font
  * @param {string} params.fontFamily - The name of the font family
  * @param {string} params.fontFile - The filename of the font (e.g., 'Gilroy-Medium.ttf')
  * @returns {Promise<FontFace>} - Resolves with the loaded FontFace object
*/
export const loadCustomFont = ({ fontFamily, fontFile }) => {
  const fontPath = `/community/fonts/${fontFile}`;
  const font = new FontFace(fontFamily, `url(${fontPath})`);

  return font.load()
    .then((loadedFont) => {
      // Add the loaded font to the document's font collection
      document.fonts.add(loadedFont);

      // Apply the custom font globally using CSS variables
      document.documentElement.style.setProperty('--custom-font', `'${fontFamily}', sans-serif`);

      // Apply the font to the body as a fallback method
      document.body.style.fontFamily = `'${fontFamily}', sans-serif`;

      // Inject a style tag to ensure all elements use the font
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        body, button, input, select, textarea, .btn, h1, h2, h3, h4, h5, h6, p, span, div {
          font-family: '${fontFamily}', sans-serif !important;
        }
      `;
      document.head.appendChild(styleEl);

      return loadedFont;
    })
    .catch((err) => {
      console.error(`Failed to load font ${fontFamily} from ${fontPath}:`, err);

      // Try with process.env.PUBLIC_URL as fallback
      const fallbackPath = `${window.location.origin}/community/fonts/${fontFile}`
      console.log(`Trying alternate path: ${fallbackPath}`);
      const fallbackFont = new FontFace(fontFamily, `url(${fallbackPath})`);
      return fallbackFont.load()
        .then((loadedFont) => {
          document.fonts.add(loadedFont);

          // Apply the custom font globally using CSS variables
          document.documentElement.style.setProperty('--custom-font', `'${fontFamily}', sans-serif`);

          // Apply the font to the body as a fallback method
          document.body.style.fontFamily = `'${fontFamily}', sans-serif`;

          // Inject a style tag to ensure all elements use the font
          const styleEl = document.createElement('style');
          styleEl.textContent = `
            body, button, input, select, textarea, .btn, h1, h2, h3, h4, h5, h6, p, span, div {
              font-family: '${fontFamily}', sans-serif !important;
            }
          `;
          document.head.appendChild(styleEl);

          console.log(`Successfully loaded font: ${fontFamily} from fallback path: ${fallbackPath}`);
          return loadedFont;
        })
        .catch((finalErr) => {
          console.error(`Failed to load font ${fontFamily} from all paths:`, finalErr);
          // Don't throw the error - allow the app to continue with fallback fonts
        });
    });
};
